import { useEffect, useState } from 'react';
import { useSyncedState } from './useSyncedState';

/** Boot gates resolve well inside this window once the request is prefetched. */
const DELAY_MS = 200;

/**
 * Gate a pending indicator behind a short delay so fast resolutions never paint.
 *
 * Returns `false` while `pending` is true but the delay hasn't elapsed — render
 * nothing then. A blank frame reads as "still loading the page"; a spinner that
 * appears and vanishes in 80ms reads as a glitch.
 *
 * Same idea as TanStack Router's `defaultPendingMs`, which the router already
 * applies to route loaders (`src/router.tsx`). This covers the auth bootstrap
 * gates, which sit above `RouterProvider` and so never reach that machinery.
 *
 * No minimum-visible counterpart on purpose: it would hold the app back after
 * auth is ready, and boot happens once per page load rather than on every
 * transition, so there is no repeated flicker to smooth out.
 */
export function useDelayedPending(pending: boolean): boolean {
  const [elapsed, setElapsed] = useState(false);

  // A new pending phase waits out the delay again instead of inheriting the
  // previous phase's elapsed timer.
  useSyncedState(pending, () => setElapsed(false));

  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(() => setElapsed(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, [pending]);

  return pending && elapsed;
}
